import React, { useMemo, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Skeleton,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import { Add, ArrowForward } from '@mui/icons-material';
import { AccountWithBalance, AccountType } from '../types/api';
import { formatCurrency } from '../utils/currency';
import { useBaseCurrency } from '../stores/currency-store';
import { InstitutionMark } from './InstitutionMark';
import { accountLabel, accountSourceName } from '../utils/account-label';
import type { AccountFreshness } from '../services/dashboard-service';
import { shortDate } from './dashboard/shared';

/** Sort and filter state, handed to /accounts so the click keeps its context. */
export interface AccountsOverviewFilters {
  sortBy: 'amount' | 'name';
  sortDir: 'asc' | 'desc';
  typeFilter: AccountType | 'All';
  institutionQuery: string;
}

interface AccountsOverviewCardProps {
  accounts: AccountWithBalance[];
  isLoading?: boolean;
  onAddAccount?: () => void;
  onViewAccount?: (account: AccountWithBalance) => void;
  /**
   * Navigate to the full accounts list. Called with the filters currently
   * applied here so the destination can honour them rather than dumping the
   * user into an unfiltered table.
   */
  onViewAllAccounts?: (filters: AccountsOverviewFilters) => void;
  /**
   * How current each balance is, keyed by account id (issue #27). Optional:
   * without it the rows render as before.
   */
  freshness?: Record<string, AccountFreshness>;
}

/**
 * "Synced today", "Value as of Aug 8", "Manual · 40d ago": the caption under a
 * balance saying how much to trust it.
 *
 * @param f The account's freshness, from the dashboard summary.
 * @returns The caption text.
 */
export function freshnessLabel(f: AccountFreshness): string {
  // An investment balance is a valuation: say when it is from, not when the
  // connection last synced (Plaid can sync daily while holdings stay old).
  if (f.basis === 'value') {
    return f.value_as_of ? `Value as of ${shortDate(f.value_as_of)}` : 'No valuation yet';
  }
  const ago = (d: number | null) =>
    d == null ? 'never' : d <= 0 ? 'today' : d === 1 ? 'yesterday' : `${d}d ago`;
  if (f.source === 'plaid') {
    return f.days_since == null ? 'Not synced yet' : `Synced ${ago(f.days_since)}`;
  }
  return f.days_since == null ? 'Manual' : `Manual · ${ago(f.days_since)}`;
}

/**
 * How many accounts the card shows before deferring to /accounts.
 *
 * Six is three rows of two. Two columns rather than three is the whole point
 * of this layout: at three columns the name box was about 100px and real
 * account names ("Questrade Non-Registered" needs 177px, "Mortgage — Fieldon"
 * 135px) truncated to two characters plus an ellipsis, which is worse than
 * the ragged wrapping the fixed-height row was introduced to fix.
 */
const PREVIEW_COUNT = 6;

/**
 * Fixed row height, in pixels.
 *
 * Fixed on purpose: names of wildly different lengths made the grid ragged,
 * and an even baseline down the column is what makes six accounts scan as a
 * list rather than as six boxes. The content is vertically centred so a row
 * with no second line still sits on the same rhythm.
 */
const ROW_HEIGHT = 64;

/**
 * Whether an institution name adds anything beside the account name.
 *
 * "Questrade RRSP" under "Questrade" is the same word twice, and the second
 * line is where the account name would rather have been. Compared on a
 * punctuation-folded, lower-cased form so "Amex Platinum"/"American Express"
 * is correctly treated as NOT redundant while "TD Chequing"/"TD" is.
 *
 * @param accountName The account's own name.
 * @param institution The institution recorded against it.
 * @returns True when the institution is worth its own line.
 */
export function institutionAddsInformation(
  accountName: string,
  institution?: string | null,
): boolean {
  const inst = (institution ?? '').trim();
  if (!inst) return false;
  const fold = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const foldedName = fold(accountName);
  const foldedInst = fold(inst);
  if (!foldedInst) return false;
  // Whole-word containment, not substring: "eq" must not match "equity".
  return !` ${foldedName} `.includes(` ${foldedInst} `);
}

export const AccountsOverviewCard: React.FC<AccountsOverviewCardProps> = ({
  accounts,
  isLoading,
  onAddAccount,
  onViewAccount,
  onViewAllAccounts,
  freshness,
}) => {
  const [sortBy, setSortBy] = useState<'amount' | 'name'>('amount');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [typeFilter, setTypeFilter] = useState<AccountType | 'All'>('All');
  const [institutionQuery, setInstitutionQuery] = useState('');
  const baseCurrency = useBaseCurrency();

  /** Everything matching the current controls, before the preview cut. */
  const matchingAccounts = useMemo(() => {
    let list = accounts.slice();
    if (typeFilter !== 'All') list = list.filter((a) => a.account_type === typeFilter);
    if (institutionQuery) {
      list = list.filter((a) =>
        a.institution?.toLowerCase().includes(institutionQuery.toLowerCase()),
      );
    }
    list.sort((a, b) => {
      if (sortBy === 'amount') {
        const av = parseFloat(a.current_amount_base || a.current_amount || '0');
        const bv = parseFloat(b.current_amount_base || b.current_amount || '0');
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      // Sorted by what is ON SCREEN. Sorting by the source name would order
      // a renamed list by labels nobody can see.
      const an = accountLabel(a);
      const bn = accountLabel(b);
      return sortDir === 'asc' ? an.localeCompare(bn) : bn.localeCompare(an);
    });
    return list;
  }, [accounts, sortBy, sortDir, typeFilter, institutionQuery]);

  const displayedAccounts = useMemo(
    () => matchingAccounts.slice(0, PREVIEW_COUNT),
    [matchingAccounts],
  );

  // Counted against what the filters actually matched, not the raw total.
  // "+26 more" while a filter is narrowing the list to eight was a lie.
  const hiddenCount = matchingAccounts.length - displayedAccounts.length;

  // An account is shown in ITS OWN currency. A USD account holds US dollars;
  // rendering it as CAD states a balance the bank never showed.
  const formatAccount = (account: AccountWithBalance) =>
    formatCurrency(account.current_amount, account.currency, { base: baseCurrency });

  const handleViewAll = () =>
    onViewAllAccounts?.({ sortBy, sortDir, typeFilter, institutionQuery });

  /** The controls, shared by the loading and loaded states. */
  const controls = (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
      <ToggleButtonGroup
        size="small"
        value={`${sortBy}-${sortDir}`}
        exclusive
        onChange={(_, v) => {
          if (!v) return;
          const [sb, sd] = v.split('-') as ['amount' | 'name', 'asc' | 'desc'];
          setSortBy(sb);
          setSortDir(sd);
        }}
      >
        <ToggleButton value="amount-desc">Amount ↓</ToggleButton>
        <ToggleButton value="amount-asc">Amount ↑</ToggleButton>
        <ToggleButton value="name-asc">Name A→Z</ToggleButton>
        <ToggleButton value="name-desc">Name Z→A</ToggleButton>
      </ToggleButtonGroup>
      <Select
        size="small"
        value={typeFilter}
        onChange={(e) => setTypeFilter(e.target.value as AccountType | 'All')}
      >
        <MenuItem value="All">All Types</MenuItem>
        <MenuItem value="Asset">Asset</MenuItem>
        <MenuItem value="Debt">Debt</MenuItem>
        <MenuItem value="Investment">Investment</MenuItem>
      </Select>
      <TextField
        size="small"
        label="Institution"
        value={institutionQuery}
        onChange={(e) => setInstitutionQuery(e.target.value)}
      />
    </Box>
  );

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Accounts Overview
          </Typography>
          {controls}
          <Grid container spacing={2}>
            {[1, 2, 3, 4].map((i) => (
              // Same breakpoints as the real rows, so the skeleton does not
              // reflow the card when the data lands.
              <Grid item xs={12} lg={6} key={i}>
                <Box
                  sx={{
                    height: ROW_HEIGHT,
                    px: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    borderTop: '1px solid var(--border-soft)',
                  }}
                >
                  <Skeleton variant="rounded" width={30} height={30} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="70%" />
                    <Skeleton variant="text" width="35%" />
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
        >
          <Typography variant="h6">Accounts Overview</Typography>
          <Button startIcon={<Add />} onClick={onAddAccount} variant="outlined" size="small">
            Add Account
          </Button>
        </Box>

        {accounts.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              No accounts yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add your first account to start tracking your net worth
            </Typography>
            <Button
              startIcon={<Add />}
              onClick={onAddAccount}
              variant="contained"
              sx={{ mt: 2 }}
            >
              Add Your First Account
            </Button>
          </Box>
        ) : (
          <>
            {controls}
            <Grid container spacing={2}>
              {displayedAccounts.map((account) => {
                const label = accountLabel(account);
                // The bank's own name, but only when a rename is hiding it.
                const sourceName = accountSourceName(account);
                const showInstitution = institutionAddsInformation(
                  label,
                  account.institution,
                );
                return (
                  // Two columns, not three -- and lg, not md.
                  //
                  // MEASURED, because guessing here is what caused the bug.
                  // "Questrade Non-Registered" needs 177px of name box at
                  // body2/600. This widget is md=8 of the dashboard grid, so
                  // the name box works out to:
                  //   two columns at a 900px viewport  -> 102px  CLIPPED
                  //   two columns at a 1200px viewport -> 202px  fits
                  //   two columns at a 1440px viewport -> 282px  fits
                  //   one column at a 900px viewport   -> 374px  fits
                  // Splitting at md would therefore have reintroduced "Qu..."
                  // between 900 and 1200px. lg keeps a single full-width
                  // column until there is genuinely room for two.
                  <Grid item xs={12} lg={6} key={account.id}>
                    <Box
                      role="button"
                      tabIndex={0}
                      onClick={() => onViewAccount?.(account)}
                      // The row was mouse-only before: an onClick on a div is
                      // unreachable by keyboard and invisible to a screen
                      // reader.
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onViewAccount?.(account);
                        }
                      }}
                      sx={{
                        // Flat ruled row, not a tile. Fixed height so every
                        // cell lines up whether or not it has a second line.
                        height: ROW_HEIGHT,
                        px: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        borderTop: '1px solid var(--border-soft)',
                        cursor: 'pointer',
                        transition: 'background 0.12s ease',
                        '&:hover': { background: 'var(--glass-1)' },
                        // Matches the app's focus convention (theme.tsx), but
                        // inset: a fixed-height ruled row has no margin for a
                        // ring to sit outside of.
                        '&:focus-visible': {
                          outline: '2px solid var(--accent)',
                          outlineOffset: '-2px',
                        },
                        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                      }}
                    >
                      {/* The institution, not the account type: a generic bank
                          glyph repeated down the column said nothing, while the
                          mark tells you at a glance which of your six accounts
                          this is. Falls back to the account name so manually
                          entered accounts still get a deliberate monogram. */}
                      <InstitutionMark
                        name={account.institution || account.name}
                        size={30}
                      />

                      {/* minWidth 0 lets the child ellipsis instead of forcing
                          the row wider, which is what pushed the amount out of
                          line. */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          title={sourceName ? `${label} (${sourceName})` : label}
                          sx={{
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {label}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: 'block',
                          }}
                        >
                          {/* The institution earns the second line only when
                              it is not already the first word of the name;
                              "Questrade" under "Questrade RRSP" spent a line
                              to say nothing. Otherwise the type goes there,
                              which is information the row does not carry
                              anywhere else. */}
                          {/* A renamed row gives the bank's own name the
                              second line: at six accounts on a dashboard, the
                              question this line answers is "which real account
                              is that", and the type is already implied by the
                              amount's sign. Falls back to the previous
                              behaviour when nothing is hidden. */}
                          {sourceName ||
                            (showInstitution ? account.institution : account.account_type)}
                        </Typography>
                      </Box>

                      {/* No type pill: the subline carries it, and the chips
                          were adding a second line on narrow columns. */}
                      <Box sx={{ flexShrink: 0, textAlign: 'right' }}>
                        <Typography
                          className="fw-num"
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.95rem',
                            color:
                              Number(account.current_amount) < 0
                                ? 'var(--danger-text)'
                                : 'var(--text)',
                          }}
                        >
                          {formatAccount(account)}
                        </Typography>
                        {freshness?.[account.id] && (
                          <Typography
                            variant="caption"
                            data-testid="account-freshness"
                            sx={{
                              display: 'block',
                              color: freshness[account.id].stale
                                ? 'var(--warn-text)'
                                : 'var(--text-faint)',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {freshnessLabel(freshness[account.id])}
                            {freshness[account.id].stale && (
                              <Box
                                component="span"
                                sx={{
                                  ml: 0.75,
                                  px: 0.6,
                                  borderRadius: 1,
                                  fontWeight: 700,
                                  fontSize: '0.65rem',
                                  letterSpacing: '0.04em',
                                  background: 'var(--warn-soft)',
                                  color: 'var(--warn-text)',
                                }}
                              >
                                STALE
                              </Box>
                            )}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
              {hiddenCount > 0 && (
                <Grid item xs={12}>
                  {/* Was a bare <Typography color="primary">: it looked like a
                      link and did nothing. A real button, so it is clickable,
                      focusable and announced as what it is. */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', pt: 0.5 }}>
                    <Button
                      size="small"
                      variant="text"
                      endIcon={<ArrowForward />}
                      onClick={handleViewAll}
                    >
                      View all {matchingAccounts.length} accounts
                      {` (${hiddenCount} more)`}
                    </Button>
                  </Box>
                </Grid>
              )}
            </Grid>
          </>
        )}
      </CardContent>
    </Card>
  );
};
